import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { 
  Bike, 
  LayoutDashboard, 
  Package, 
  Plus, 
  Image, 
  Settings,
  ChevronRight,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  SidebarProvider, 
  Sidebar, 
  SidebarContent, 
  SidebarFooter, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarGroup as SidebarGroupComponent,
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton, 
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

// --- Sidebar Data Structure ---
const sidebarGroups = [
  {
    label: 'Main',
    items: [
      { title: 'Dashboard', url: '/admin', icon: LayoutDashboard },
      { title: 'Back to Site', url: '/', icon: Bike },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        title: 'Products',
        url: '/admin/products',
        icon: Package,
        children: [],
      },
      {
        title: 'Motorcycles',
        url: '/admin/motorcycles',
        icon: Package,
        children: [],
      },
      { title: 'Add Product', url: '/admin/products/add', icon: Plus },
      { title: 'Add Motorcycle', url: '/admin/motorcycles/add', icon: Plus },
    ],
  },
  {
    label: 'Settings',
    items: [
      {
        title: 'Motorcycle Makes',
        url: null,
        icon: Settings,
        children: [
          { title: 'Makes', url: '/admin/settings/makes' },
          { title: 'Models', url: '/admin/settings/models' },
        ],
      },
      { title: 'Hero Carousel', url: '/admin/carousel', icon: Image },
    ],
  },
];

// --- Sidebar Component (internal) ---
function AppSidebar() {
  const location = useLocation();
  const { state, openMobile, setOpenMobile } = useSidebar();
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const isGroupCollapsed = (label) => {
    if (state === 'collapsed') return true;
    return collapsedGroups[label] !== false;
  };

  const toggleGroup = (label) => {
    setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (url) => {
    if (!url) return false;
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  const handleMenuClick = (url) => {
    if (openMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar className="gap-0 border-r">
      {/* Sidebar Header / Branding */}
      <SidebarHeader className="border-b p-4">
        <Link to="/" className="flex items-center gap-2" onClick={() => handleMenuClick('/')}>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Bike className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Linduska</div>
            <div className="text-xs text-slate-500">Admin Panel</div>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        {sidebarGroups.map((group, groupIndex) => (
          <SidebarGroupComponent key={group.label} className="px-2 py-2">
            <SidebarGroupLabel className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {group.items.map((item, itemIndex) => {
                  const Icon = item.icon;
                  const active = isActive(item.url);
                  const hasChildren = item.children && item.children.length > 0;
                  const collapsed = isGroupCollapsed(`${group.label}-${item.title}`);
                  const isCollapsed = state === 'collapsed';

                  if (hasChildren && !isCollapsed) {
                    return (
                      <SidebarMenuItem key={`${group.label}-${item.title}`}>
                        <Collapsible
                          open={!collapsed}
                          onOpenChange={() => toggleGroup(`${group.label}-${item.title}`)}
                        >
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              className={cn(
                                'justify-between font-normal',
                                active && 'bg-indigo-50 text-indigo-600 font-medium'
                              )}
                            >
                              <span className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                <span>{item.title}</span>
                              </span>
                              <ChevronRight
                                className={cn(
                                  "h-4 w-4 transition-transform text-slate-400",
                                  !collapsed && "rotate-90"
                                )}
                              />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenu className="ml-6 border-l border-slate-100 py-1">
                              {item.children.map((child) => (
                                <SidebarMenuItem key={child.title}>
                                  <SidebarMenuButton
                                    asChild
                                    isActive={isActive(child.url)}
                                    className={cn(
                                      isActive(child.url) && 'bg-indigo-50 text-indigo-600 font-medium'
                                    )}
                                  >
                                    <Link to={child.url} onClick={() => handleMenuClick(child.url)}>
                                      <span>{child.title}</span>
                                    </Link>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                              ))}
                            </SidebarMenu>
                          </CollapsibleContent>
                        </Collapsible>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={`${group.label}-${item.title}`}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.title}
                        className={cn(
                          active && 'bg-indigo-50 text-indigo-600 font-medium'
                        )}
                      >
                        <Link to={item.url || '#'} onClick={() => handleMenuClick(item.url)}>
                          <Icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroupComponent>
        ))}
      </SidebarContent>

      {/* Sidebar Footer */}
      <SidebarFooter className="border-t p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50 transition-colors">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
              A
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">Admin User</p>
            <p className="text-xs text-slate-400 truncate">admin@linduska.com</p>
          </div>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

// --- Mobile Header with Menu Toggle ---
function MobileHeader() {
  const { openMobile, setOpenMobile } = useSidebar();
  
  return (
    <div className="flex items-center gap-2 p-4 border-b bg-white md:hidden">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => setOpenMobile(!openMobile)}
      >
        <Menu className="h-5 w-5" />
      </Button>
      <Link to="/" className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded bg-indigo-600 text-white">
          <Bike className="h-4 w-4" />
        </div>
        <span className="font-semibold text-slate-900">Linduska Admin</span>
      </Link>
    </div>
  );
}

// --- Main Admin Layout ---
export default function AdminLayout() {
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full bg-slate-50">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <MobileHeader />
          <main className="flex-1 overflow-y-auto">
            <div className="hidden md:block p-6 md:p-8">
              <Outlet />
            </div>
            <div className="md:hidden p-4">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}